import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  api,
  ProfileComplete,
  ProfileData,
  FollowersAndStories,
} from "@/services/api";
import { FeedMedia } from "@/types/feed-media";
import { storage } from "@/services/storage";
import { useEffect, useState } from "react";

// Query keys
export const queryKeys = {
  profileData: (username: string) => ["profileData", username],
  feedMedia: (username: string) => ["feedMedia", username],
  followersAndStories: (username: string) => ["followersAndStories", username],
  profileComplete: (username: string) => ["profileComplete", username],
};

// Individual queries
export const useProfileDataQuery = (username: string) => {
  return useQuery({
    queryKey: queryKeys.profileData(username),
    queryFn: () => api.fetchProfileData(username),
    enabled: !!username,
    staleTime: Infinity,
  });
};

export const useFeedMediaQuery = (username: string) => {
  return useQuery({
    queryKey: queryKeys.feedMedia(username),
    queryFn: () => api.fetchFeedMedia(username),
    enabled: !!username,
    staleTime: Infinity,
  });
};

export const useFollowersAndStoriesQuery = (username: string) => {
  return useQuery({
    queryKey: queryKeys.followersAndStories(username),
    queryFn: () => api.fetchFollowersAndStories(username),
    enabled: !!username,
    staleTime: Infinity,
  });
};

// Combined query hook with persistence
export const useProfileCompleteQuery = (
  username: string,
  type: string,
): {
  data: ProfileComplete | undefined;
  isLoading: boolean;
  isError: boolean;
  allSuccess: boolean;
  error: Error | null;
  progress: number;
  hasSearchedBefore: boolean;
} => {
  const queryClient = useQueryClient();
  const hasSearchedBefore = storage.hasSearchedBefore();

  // Try to get cached data first
  useEffect(() => {
    if (hasSearchedBefore) {
      const cachedData = storage.getProfileData();
      if (cachedData) {
        queryClient.setQueryData(
          queryKeys.profileComplete(cachedData.profileData.username),
          cachedData,
        );
        queryClient.setQueryData(
          queryKeys.profileData(cachedData.profileData.username),
          cachedData.profileData,
        );
        queryClient.setQueryData(
          queryKeys.feedMedia(cachedData.profileData.username),
          cachedData.feedMedia,
        );
        queryClient.setQueryData(
          queryKeys.followersAndStories(cachedData.profileData.username),
          cachedData.followersAndStories,
        );
      }
    }
  }, [hasSearchedBefore, queryClient]);

  // State to track progress for sequential loading
  const [currentProgress, setCurrentProgress] = useState(0);

  const profileDataQuery = useQuery<ProfileData, Error>({
    queryKey: queryKeys.profileData(username),
    queryFn: () => api.fetchProfileData(username),
    enabled: !!username && !hasSearchedBefore,
    staleTime: Infinity,
  });

  const feedMediaQuery = useQuery<FeedMedia, Error>({
    queryKey: queryKeys.feedMedia(username),
    queryFn: () => api.fetchFeedMedia(username),
    enabled: !!username && !hasSearchedBefore && profileDataQuery.isSuccess, // Enabled only after profileData
    staleTime: Infinity,
  });

  const followersAndStoriesQuery = useQuery<FollowersAndStories, Error>({
    queryKey: queryKeys.followersAndStories(username),
    queryFn: () => api.fetchFollowersAndStories(username),
    enabled: !!username && !hasSearchedBefore && feedMediaQuery.isSuccess, // Enabled only after feedMedia
    staleTime: Infinity,
  });

  // Use useEffect to update progress based on query states
  useEffect(() => {
    if (profileDataQuery.isSuccess) {
      setCurrentProgress(33);
      if (feedMediaQuery.isSuccess) {
        setCurrentProgress(66);
        if (followersAndStoriesQuery.isSuccess) {
          setCurrentProgress(100);
        } else if (followersAndStoriesQuery.isError) {
          setCurrentProgress(66); // Reverte para o estado anterior se a última falhar
        }
      } else if (feedMediaQuery.isError) {
        setCurrentProgress(33); // Reverte para o estado anterior se a do meio falhar
      }
    } else if (profileDataQuery.isError) {
      setCurrentProgress(0);
    }
  }, [
    profileDataQuery.isSuccess,
    profileDataQuery.isError,
    feedMediaQuery.isSuccess,
    feedMediaQuery.isError,
    followersAndStoriesQuery.isSuccess,
    followersAndStoriesQuery.isError,
  ]);

  const queries = [profileDataQuery, feedMediaQuery, followersAndStoriesQuery];

  // Calculate overall progress based on individual query states
  let progress = currentProgress;

  if (hasSearchedBefore) {
    progress = 100;
  } else if (queries.some((q) => q.isError)) {
    progress = 0; // Se qualquer query der erro, reseta o progresso
  }

  // Combine the data when all queries are successful
  const allSuccess =
    profileDataQuery.isSuccess &&
    feedMediaQuery.isSuccess &&
    followersAndStoriesQuery.isSuccess;

  const isLoading = queries.some((q) => q.isLoading);
  const isError = queries.some((q) => q.isError);
  const error = isError
    ? ((profileDataQuery.error ||
        feedMediaQuery.error ||
        followersAndStoriesQuery.error) as Error)
    : null;

  let data: ProfileComplete | undefined;

  if (hasSearchedBefore) {
    data = storage.getProfileData() || undefined;
  } else if (allSuccess) {
    data = {
      profileData: profileDataQuery.data as ProfileData,
      feedMedia: feedMediaQuery.data as FeedMedia,
      followersAndStories: followersAndStoriesQuery.data as FollowersAndStories,
      searchedAt: Date.now(),
      searchType: type,
    };

    if (data) {
      storage.saveProfileData(data);
    }
  }

  return {
    data,
    isLoading,
    isError,
    allSuccess,
    error,
    progress,
    hasSearchedBefore,
  };
};
